import { Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { filterCandidates, RowCandidate, RowPickerData, RowPickerResult } from '../bu-pdf-bulk-upload-review';

/** The most rows listed at once; the user narrows a larger index down with the search box. */
export const ROW_PICKER_MAX_SHOWN: number = 100;

/**
 * Lets the user choose which rows of the index refer to a file that no row referenced. Nothing is chosen for the user: the file is only
 * associated with the rows that are ticked, or with a new row when the user asks for one.
 */
@Component({
  selector: 'app-bu-pdf-row-picker-dialog',
  templateUrl: './bu-pdf-row-picker-dialog.component.html',
  styleUrls: ['./bu-pdf-row-picker-dialog.component.scss'],
  standalone: false,
})
export class BuPdfRowPickerDialogComponent {
  private readonly dialogRef: MatDialogRef<BuPdfRowPickerDialogComponent, RowPickerResult> = inject(MatDialogRef);
  readonly data: RowPickerData = inject<RowPickerData>(MAT_DIALOG_DATA);
  readonly maxShown: number = ROW_PICKER_MAX_SHOWN;

  readonly search: WritableSignal<string> = signal<string>('');
  readonly selectedRowIds: WritableSignal<ReadonlySet<number>> = signal<ReadonlySet<number>>(new Set<number>());
  readonly matches: Signal<RowCandidate[]> = computed(() => filterCandidates(this.data.candidates, this.search()));
  readonly shown: Signal<RowCandidate[]> = computed(() => this.matches().slice(0, ROW_PICKER_MAX_SHOWN));

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  isSelected(rowId: number): boolean {
    return this.selectedRowIds().has(rowId);
  }

  toggle(rowId: number): void {
    this.selectedRowIds.update((selected: ReadonlySet<number>) => {
      const next: Set<number> = new Set<number>(selected);
      if (!next.delete(rowId)) {
        next.add(rowId);
      }
      return next;
    });
  }

  trackByRowId(_index: number, candidate: RowCandidate): number {
    return candidate.rowId;
  }

  associate(): void {
    this.dialogRef.close({ rowIds: [...this.selectedRowIds()], addNewRow: false });
  }

  addNewRow(): void {
    this.dialogRef.close({ rowIds: [...this.selectedRowIds()], addNewRow: true });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }
}
